import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { ResolvedParsedPosition } from '../ImportDataModal';
import { PositionTrancheData } from './ExistingPositionsScreen';
import { getPositions, getPortfolio, createPosition, addTranche } from '../../services/api';
import { Position, Broker } from '../../types';
import { analytics } from '../../lib/analytics';

interface ParsedTranche {
  orderId: string;
  symbol: string;
  isin: string;
  type: 'buy' | 'sell';
  quantity: number;
  avgPrice: number;
  timestamp: string;
  exchange: string;
  isAlreadyImported: boolean;
}

// Extended tranche with selection and stoploss data
interface TrancheWithSelection extends ParsedTranche {
  selected: boolean;           // Whether this tranche is selected for import
  stoplossValue: string;       // Stoploss value for buy tranches
  maintainResidualRisk: boolean;
  calculatedStoploss: number | null;
}

interface NewPositionsScreenProps {
  parsedData: ResolvedParsedPosition[];
  portfolioId: string;
  broker: Broker;
  existingPositionsData: PositionTrancheData[];
  onComplete: () => void;
  onBack: () => void;
}

interface NewPositionData {
  isin: string;
  ticker: string;
  exchange: 'NSE' | 'BSE';
  bse_scrip_code?: string;
  name?: string;
  allTranches: TrancheWithSelection[];  // All tranches sorted chronologically
  category: string;
  selected: boolean;  // Position-level selection (all tranches)
}

const NewPositionsScreen: React.FC<NewPositionsScreenProps> = ({
  parsedData,
  portfolioId,
  broker,
  existingPositionsData,
  onComplete,
  onBack,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newPositions, setNewPositions] = useState<NewPositionData[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [portfolioCategories, setPortfolioCategories] = useState<Array<{name: string; color: string}>>([]);
  const [defaultCategory, setDefaultCategory] = useState<string>('');
  const [overriddenPositions, setOverriddenPositions] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadPortfolioAndPositions();
  }, [portfolioId, parsedData]);

  const loadPortfolioAndPositions = async () => {
    setIsLoading(true);
    
    try {
      // Load portfolio to get categories with colors
      const portfolioResponse = await getPortfolio(portfolioId);
      if (portfolioResponse.success && portfolioResponse.data) {
        const categories = portfolioResponse.data.categories?.map(cat => ({
          name: cat.name,
          color: cat.color
        })) || [];
        setPortfolioCategories(categories);
      }
      
      // Load positions to identify new ones (match by ISIN)
      const response = await getPositions(portfolioId);
      
      if (response.success) {
        const existingPositions = response.data.positions;
        const existingIsins = new Set(existingPositions.map(p => p.isin));
        
        // Filter for new positions (ISIN not in existing)
        const newPosData: NewPositionData[] = [];
        
        for (const parsed of parsedData) {
          if (!existingIsins.has(parsed.isin)) {
            // Sort all tranches chronologically
            const sortedTranches = [...parsed.tranches].sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            
            // Check if there are any new tranches (buy or sell)
            const hasNewTranches = sortedTranches.some(t => !t.isAlreadyImported);
            
            // Create tranches with selection and stoploss data
            let newBuyTrancheIndex = 0;
            const tranchesWithSelection: TrancheWithSelection[] = sortedTranches.map((tranche) => {
              const isNew = !tranche.isAlreadyImported;
              const isBuy = tranche.type === 'buy';
              
              // Default 10% stoploss for first new buy tranche
              let defaultStoploss = '';
              if (isNew && isBuy) {
                if (newBuyTrancheIndex === 0) {
                  defaultStoploss = String(Math.round(tranche.avgPrice * 0.9 * 100) / 100);
                }
                newBuyTrancheIndex++;
              }
              
              return {
                ...tranche,
                selected: isNew,  // New tranches selected by default, duplicates not
                stoplossValue: defaultStoploss,
                maintainResidualRisk: false,
                calculatedStoploss: null,
              };
            });
            
            // Only add positions that have tranches
            if (tranchesWithSelection.length > 0) {
              newPosData.push({
                isin: parsed.isin,
                ticker: parsed.ticker,
                exchange: parsed.exchange,
                bse_scrip_code: parsed.bse_scrip_code,
                name: parsed.name,
                allTranches: tranchesWithSelection,
                category: '',
                selected: hasNewTranches,  // Position selected if there are any new tranches
              });
            }
          }
        }
        
        setNewPositions(newPosData);
      }
    } catch (error) {
      console.error('Error loading portfolio and positions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle individual tranche selection
  const handleToggleTrancheSelection = (posIndex: number, trancheIndex: number, checked: boolean) => {
    setNewPositions(prev => {
      const updated = [...prev];
      const tranche = updated[posIndex].allTranches[trancheIndex];
      
      // Can't select already imported tranches
      if (tranche.isAlreadyImported) return prev;
      
      tranche.selected = checked;
      
      // Update position-level selection based on whether any new tranche is selected
      const hasSelectedTranche = updated[posIndex].allTranches.some(
        t => t.selected && !t.isAlreadyImported
      );
      updated[posIndex].selected = hasSelectedTranche;
      
      return updated;
    });
  };

  // Toggle all tranches in a position
  const handleTogglePositionSelection = (posIndex: number, checked: boolean) => {
    setNewPositions(prev => {
      const updated = [...prev];
      
      // Select/deselect all new tranches in this position
      updated[posIndex].allTranches.forEach(tranche => {
        if (!tranche.isAlreadyImported) {
          tranche.selected = checked;
        }
      });
      updated[posIndex].selected = checked;
      
      return updated;
    });
  };

  const handleCategoryChange = (posIndex: number, category: string) => {
    setNewPositions(prev => {
      const updated = [...prev];
      updated[posIndex].category = category;
      return updated;
    });
    
    // Mark this position as overridden
    setOverriddenPositions(prev => new Set(prev).add(posIndex));
    
    setValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[`${posIndex}-category`];
      return updated;
    });
  };

  // Handle default category change - apply to all non-overridden positions
  const handleDefaultCategoryChange = (category: string) => {
    setDefaultCategory(category);
    
    setNewPositions(prev => {
      const updated = [...prev];
      updated.forEach((pos, index) => {
        // Only update positions that haven't been manually overridden
        if (!overriddenPositions.has(index)) {
          pos.category = category;
        }
      });
      return updated;
    });
    
    // Clear category validation errors for non-overridden positions
    setValidationErrors(prev => {
      const updated = { ...prev };
      // Use Object.keys to iterate over error keys and check if they're category errors
      Object.keys(updated).forEach(key => {
        if (key.endsWith('-category')) {
          const posIndex = parseInt(key.split('-')[0], 10);
          if (!overriddenPositions.has(posIndex)) {
            delete updated[key];
          }
        }
      });
      return updated;
    });
  };

  const handleStoplossChange = (posIndex: number, trancheIndex: number, value: string) => {
    setNewPositions(prev => {
      const updated = [...prev];
      updated[posIndex].allTranches[trancheIndex].stoplossValue = value;
      updated[posIndex].allTranches[trancheIndex].maintainResidualRisk = false;
      updated[posIndex].allTranches[trancheIndex].calculatedStoploss = null;
      return updated;
    });
    
    setValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[`${posIndex}-stoploss`];
      return updated;
    });
  };

  const calculateResidualRiskForTranche = (
    position: NewPositionData,
    currentTrancheIndex: number
  ): number => {
    const tranches = position.allTranches;
    
    // Get all selected buy tranches up to and including current tranche
    let totalInvestment = 0;
    let totalQuantity = 0;
    let riskAmount = 0;
    
    for (let i = 0; i <= currentTrancheIndex; i++) {
      const tranche = tranches[i];
      if (tranche.type !== 'buy' || !tranche.selected || tranche.isAlreadyImported) continue;
      
      const trancheInvestment = tranche.avgPrice * tranche.quantity;
      totalInvestment += trancheInvestment;
      totalQuantity += tranche.quantity;
      
      // Use the stoploss from this tranche if available
      if (tranche.stoplossValue && parseFloat(tranche.stoplossValue) > 0) {
        riskAmount = (tranche.avgPrice - parseFloat(tranche.stoplossValue)) * tranche.quantity;
      }
    }
    
    if (totalQuantity === 0) return 0;
    
    const newStoploss = (totalInvestment - riskAmount) / totalQuantity;
    return Math.round(newStoploss * 100) / 100;
  };

  const handleResidualRiskToggle = (posIndex: number, trancheIndex: number, checked: boolean) => {
    setNewPositions(prev => {
      const updated = [...prev];
      const position = updated[posIndex];
      const tranche = position.allTranches[trancheIndex];
      
      tranche.maintainResidualRisk = checked;
      
      if (checked) {
        const calculatedSL = calculateResidualRiskForTranche(position, trancheIndex);
        tranche.calculatedStoploss = calculatedSL;
        tranche.stoplossValue = '';
      } else {
        tranche.calculatedStoploss = null;
      }
      
      return updated;
    });
  };

  // Find if there's a previous buy tranche with stoploss
  const hasPreviousBuyWithStoploss = (position: NewPositionData, currentIndex: number): boolean => {
    for (let i = 0; i < currentIndex; i++) {
      const t = position.allTranches[i];
      if (t.type === 'buy' && t.selected && !t.isAlreadyImported) {
        if ((t.stoplossValue && parseFloat(t.stoplossValue) > 0) || t.maintainResidualRisk) {
          return true;
        }
      }
    }
    return false;
  };

  // Check if this is the first selected buy tranche
  const isFirstSelectedBuy = (position: NewPositionData, currentIndex: number): boolean => {
    for (let i = 0; i < currentIndex; i++) {
      const t = position.allTranches[i];
      if (t.type === 'buy' && t.selected && !t.isAlreadyImported) {
        return false;
      }
    }
    return true;
  };

  // Find the latest selected buy tranche index
  const getLatestSelectedBuyIndex = (position: NewPositionData): number => {
    let lastIndex = -1;
    for (let i = 0; i < position.allTranches.length; i++) {
      const t = position.allTranches[i];
      if (t.type === 'buy' && t.selected && !t.isAlreadyImported) {
        lastIndex = i;
      }
    }
    return lastIndex;
  };

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    console.log('Validating form...', newPositions.length, 'positions');
    
    newPositions.forEach((position, posIndex) => {
      if (!position.selected) {
        console.log(`Position ${posIndex} (${position.ticker}) - not selected, skipping`);
        return;
      }
      
      console.log(`Validating position ${posIndex} (${position.ticker}):`, {
        category: position.category,
        selected: position.selected,
        tranchesCount: position.allTranches.length
      });
      
      // Check category
      if (!position.category) {
        errors[`${posIndex}-category`] = `${position.ticker}: Category is required`;
        console.log(`  ❌ Missing category for ${position.ticker}`);
      }
      
      // Check latest selected buy tranche has stoploss
      const latestBuyIndex = getLatestSelectedBuyIndex(position);
      console.log(`  Latest buy index: ${latestBuyIndex}`);
      
      if (latestBuyIndex >= 0) {
        const latestBuy = position.allTranches[latestBuyIndex];
        const hasStoploss = (latestBuy.stoplossValue && parseFloat(latestBuy.stoplossValue) > 0) || 
                           latestBuy.maintainResidualRisk;
        console.log(`  Latest buy stoploss: ${latestBuy.stoplossValue}, maintainResidualRisk: ${latestBuy.maintainResidualRisk}, hasStoploss: ${hasStoploss}`);
        if (!hasStoploss) {
          errors[`${posIndex}-stoploss`] = `${position.ticker}: Stoploss is required for latest buy tranche`;
          console.log(`  ❌ Missing stoploss for ${position.ticker}`);
        }
      }
    });
    
    console.log('Validation errors:', errors);
    setValidationErrors(errors);
    return errors;
  };

  const handleImport = async () => {
    console.log('handleImport called');
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      console.log('Validation failed, not proceeding with import');
      setShowValidationSummary(true);
      // Scroll to top of the form to show validation summary
      const scrollContainer = document.querySelector('.max-h-\\[500px\\]');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
      return;
    }
    
    setShowValidationSummary(false);
    console.log('Validation passed, starting import...');
    setIsSaving(true);
    
    try {
      // First, process existing positions
      console.log(`Processing ${existingPositionsData.length} existing positions...`);
      for (const existingPos of existingPositionsData) {
        if (!existingPos.selected) {
          console.log(`Skipping ${existingPos.ticker} (existing) - not selected`);
          continue;
        }
        
        // Sort tranches chronologically
        const sortedTranches = [...existingPos.newTranches].sort((a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        
        for (const tranche of sortedTranches) {
          let trancheStoploss: number | undefined;
          
          if (tranche.type === 'buy') {
            // Find stoploss data for this buy tranche
            const buyIndex = existingPos.newTranches
              .filter(t => t.type === 'buy')
              .findIndex(t => t.orderId === tranche.orderId);
            
            if (buyIndex >= 0) {
              const slData = existingPos.stoplossData[buyIndex];
              if (slData) {
                if (slData.value && parseFloat(slData.value) > 0) {
                  trancheStoploss = parseFloat(slData.value);
                } else if (slData.maintainResidualRisk && slData.calculatedValue) {
                  trancheStoploss = slData.calculatedValue;
                }
              }
            }
          }
          
          const addResponse = await addTranche(portfolioId, existingPos.positionId, {
            orderId: tranche.orderId,
            type: tranche.type,
            quantity: tranche.quantity,
            price: tranche.avgPrice,
            broker: broker,
            timestamp: tranche.timestamp,
            stoploss: tranche.type === 'buy' ? trancheStoploss : undefined,
          });
          
          if (addResponse.success === false) {
            console.error(`Failed to add tranche to ${existingPos.ticker}:`, addResponse.error);
          } else {
            console.log(`✓ Added ${tranche.type} tranche to ${existingPos.ticker}`);
          }
        }
      }
      
      // Then, process new positions
      for (const position of newPositions) {
        if (!position.selected) {
          console.log(`Skipping ${position.ticker} - not selected`);
          continue;
        }
        
        // Get only selected tranches, sorted chronologically
        const selectedTranches = position.allTranches
          .filter(t => t.selected && !t.isAlreadyImported)
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        
        const hasSelectedBuys = selectedTranches.some(t => t.type === 'buy');
        if (!hasSelectedBuys) {
          console.warn(`Skipping ${position.ticker} - no selected buy tranches`);
          continue;
        }
        
        // Create position
        console.log(`Creating new position for ${position.ticker} (ISIN: ${position.isin})...`);
        const createResponse = await createPosition(portfolioId, {
          isin: position.isin,
          ticker: position.ticker,
          name: position.name,
          category: position.category,
          broker: broker,
          exchange: {
            name: position.exchange,
            bse_scrip_code: position.bse_scrip_code,
          },
        });
        
        if (createResponse.success === false) {
          console.error(`Failed to create position for ${position.ticker}:`, createResponse.error);
          continue;
        }
        
        const positionId = createResponse.data?.id;
        if (!positionId) {
          console.error(`No position ID returned for ${position.ticker}`);
          continue;
        }
        
        console.log(`Created position for ${position.ticker}, adding ${selectedTranches.length} tranches...`);
        
        // Add selected tranches in chronological order
        for (const tranche of selectedTranches) {
          let trancheStoploss: number | undefined;
          
          if (tranche.type === 'buy') {
            if (tranche.stoplossValue && parseFloat(tranche.stoplossValue) > 0) {
              trancheStoploss = parseFloat(tranche.stoplossValue);
            } else if (tranche.maintainResidualRisk && tranche.calculatedStoploss) {
              trancheStoploss = tranche.calculatedStoploss;
            }
          }
          
          const addResponse = await addTranche(portfolioId, positionId, {
            orderId: tranche.orderId,
            type: tranche.type,
            quantity: tranche.quantity,
            price: tranche.avgPrice,
            broker: broker,
            timestamp: tranche.timestamp,
            stoploss: tranche.type === 'buy' ? trancheStoploss : undefined,
          });
          
          if (addResponse.success === false) {
            console.error(`Failed to add ${tranche.type} tranche for ${position.ticker}:`, addResponse.error);
          } else {
            console.log(`✓ Added ${tranche.type} tranche for ${position.ticker}`);
          }
        }
        
        console.log(`Completed import for ${position.ticker}`);
      }
      
      // Track import completion
      const selectedNewPositions = newPositions.filter(p => p.selected);
      const totalTranches = selectedNewPositions.reduce((sum, pos) => 
        sum + pos.allTranches.filter(t => t.selected && !t.isAlreadyImported).length, 0
      );
      analytics.trackImportCompleted(broker, portfolioId, totalTranches, selectedNewPositions.length);
      
      onComplete();
    } catch (error) {
      console.error('Error importing positions:', error);
      alert('Failed to import some positions. Please check console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  // Count selected tranches
  const getSelectedCounts = (position: NewPositionData) => {
    const newTranches = position.allTranches.filter(t => !t.isAlreadyImported);
    const selectedTranches = position.allTranches.filter(t => t.selected && !t.isAlreadyImported);
    const duplicateTranches = position.allTranches.filter(t => t.isAlreadyImported);
    return { newTranches, selectedTranches, duplicateTranches };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        <span className="ml-2 text-gray-600">Identifying new stocks...</span>
      </div>
    );
  }

  // Calculate total selected tranches from BOTH existing and new positions
  const existingPositionsTranches = existingPositionsData.reduce((sum, p) => 
    sum + (p.newTranches?.length || 0), 0
  );
  const existingPositionsCount = existingPositionsData.filter(p => p.selected).length;
  
  const newPositionsTranches = newPositions.reduce((sum, p) => 
    sum + p.allTranches.filter(t => t.selected && !t.isAlreadyImported).length, 0
  );
  const newPositionsCount = newPositions.filter(p => p.selected).length;
  
  const totalSelectedTranches = existingPositionsTranches + newPositionsTranches;
  const totalSelectedPositions = existingPositionsCount + newPositionsCount;

  if (newPositions.length === 0) {
    return (
      <div className="py-6">
        <p className="text-center text-gray-600 mb-6">
          No new stocks to add. {existingPositionsTranches > 0 
            ? `${existingPositionsTranches} tranche(s) from existing positions will be imported.`
            : 'All stocks already have open positions.'}
        </p>
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={onBack}>
            ← Back
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={isSaving || totalSelectedTranches === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : totalSelectedTranches > 0 ? (
              `Import ${totalSelectedTranches} Tranche(s)`
            ) : (
              'Complete Import'
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        New stocks being added
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Select tranches to import and set stoploss for buy tranches. Tranches are shown in chronological order.
      </p>

      {/* Default Category Selector */}
      <Card className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-shrink-0">
            <Label className="text-sm font-semibold text-gray-900">
              Default Category for All Positions:
            </Label>
            <p className="text-xs text-gray-500 mt-0.5">
              You can override individual positions below
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {portfolioCategories.length > 0 ? (
              portfolioCategories.map((cat) => (
                <button
                  key={cat.name}
                  type="button"
                  onClick={() => handleDefaultCategoryChange(cat.name)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    defaultCategory === cat.name
                      ? 'ring-2 ring-offset-2 shadow-md'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    backgroundColor: defaultCategory === cat.name ? cat.color : `${cat.color}40`,
                    color: defaultCategory === cat.name ? '#ffffff' : cat.color,
                    '--tw-ring-color': cat.color,
                  } as React.CSSProperties}
                >
                  {cat.name}
                </button>
              ))
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleDefaultCategoryChange('Core')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    defaultCategory === 'Core'
                      ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2 shadow-md'
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                >
                  Core
                </button>
                <button
                  type="button"
                  onClick={() => handleDefaultCategoryChange('Satellite')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    defaultCategory === 'Satellite'
                      ? 'bg-green-600 text-white ring-2 ring-green-600 ring-offset-2 shadow-md'
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
                >
                  Satellite
                </button>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Validation Error Summary Banner */}
      {showValidationSummary && Object.keys(validationErrors).length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800 mb-2">
                Please fix the following errors before importing:
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                {Object.values(validationErrors).map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
        {newPositions.map((position, posIndex) => {
          const { newTranches, selectedTranches, duplicateTranches } = getSelectedCounts(position);
          const hasNewTranches = newTranches.length > 0;
          const latestBuyIndex = getLatestSelectedBuyIndex(position);
          
          return (
            <Card key={position.isin} className={`p-6 ${!position.selected ? 'bg-gray-50' : ''}`}>
              {/* Position Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={`select-pos-${posIndex}`}
                      checked={position.selected}
                      disabled={!hasNewTranches}
                      onCheckedChange={(checked) => handleTogglePositionSelection(posIndex, checked as boolean)}
                      className="cursor-pointer"
                    />
                    <label htmlFor={`select-pos-${posIndex}`} className="cursor-pointer">
                      <h4 className={`text-lg font-semibold ${position.selected ? 'text-gray-900' : 'text-gray-500'}`}>
                        {position.ticker}
                      </h4>
                    </label>
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                      {position.exchange}
                    </span>
                  </div>
                  <p className={`text-sm ml-8 ${position.selected ? 'text-gray-600' : 'text-gray-400'}`}>
                    New Position - {position.allTranches.length} tranche(s) in CSV
                  </p>
                  <div className="ml-8 flex items-center gap-3 mt-1">
                    {selectedTranches.length > 0 && (
                      <span className="text-sm text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {selectedTranches.length} selected for import
                      </span>
                    )}
                    {duplicateTranches.length > 0 && (
                      <span className="text-sm text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {duplicateTranches.length} already imported
                      </span>
                    )}
                  </div>
                  <p className={`text-xs ml-8 ${position.selected ? 'text-gray-500' : 'text-gray-400'}`}>
                    ISIN: {position.isin}
                  </p>
                </div>
              </div>

              {/* Tranches - Chronological Order */}
              <div className={`space-y-3 mb-6 ${!position.selected && hasNewTranches ? 'opacity-40' : ''}`}>
                <p className="font-medium text-gray-700">Tranches:</p>
                
                {position.allTranches.map((tranche, trancheIndex) => {
                  const isAlreadyImported = tranche.isAlreadyImported;
                  const isBuy = tranche.type === 'buy';
                  const isLatestBuy = trancheIndex === latestBuyIndex;
                  const isFirstBuy = isBuy && !isAlreadyImported && tranche.selected && isFirstSelectedBuy(position, trancheIndex);
                  const showResidualRisk = isBuy && !isAlreadyImported && tranche.selected && !isFirstBuy && hasPreviousBuyWithStoploss(position, trancheIndex);
                  
                  return (
                    <div
                      key={tranche.orderId}
                      className={`border-l-4 pl-4 py-3 ${
                        isAlreadyImported 
                          ? 'border-amber-300 bg-amber-50 opacity-60' 
                          : tranche.selected
                            ? isLatestBuy
                              ? 'border-emerald-500 bg-emerald-50'
                              : isBuy
                                ? 'border-blue-300 bg-blue-50'
                                : 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start gap-3">
                          {/* Tranche Selection Checkbox */}
                          <Checkbox
                            id={`tranche-${posIndex}-${trancheIndex}`}
                            checked={tranche.selected}
                            disabled={isAlreadyImported || !position.selected}
                            onCheckedChange={(checked) => handleToggleTrancheSelection(posIndex, trancheIndex, checked as boolean)}
                            className="mt-1 cursor-pointer"
                          />
                          <div>
                            <p className="font-medium text-gray-900">
                              {new Date(tranche.timestamp).toLocaleDateString()} - {' '}
                              <span className={isBuy ? 'text-blue-600' : 'text-red-600'}>
                                {isBuy ? 'BUY' : 'SELL'}
                              </span>
                              {isLatestBuy && (
                                <span className="ml-2 text-emerald-600 text-sm">★ LATEST BUY</span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600">
                              {tranche.quantity} shares @ ₹{tranche.avgPrice}
                            </p>
                            <p className="text-xs text-gray-500">
                              Order ID: {tranche.orderId}
                            </p>
                          </div>
                        </div>
                        {isAlreadyImported && (
                          <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded whitespace-nowrap">
                            Already Imported
                          </span>
                        )}
                      </div>

                      {/* Stoploss Input - Only for selected new buy tranches */}
                      {isBuy && !isAlreadyImported && tranche.selected && (
                        <div className="ml-8 space-y-3">
                          {/* Residual Risk Option (for non-first tranches) */}
                          {showResidualRisk && (
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`residual-${posIndex}-${trancheIndex}`}
                                checked={tranche.maintainResidualRisk}
                                onCheckedChange={(checked) =>
                                  handleResidualRiskToggle(posIndex, trancheIndex, checked as boolean)
                                }
                              />
                              <label
                                htmlFor={`residual-${posIndex}-${trancheIndex}`}
                                className="text-sm font-medium leading-none"
                              >
                                Maintain Residual Risk
                                {tranche.maintainResidualRisk && tranche.calculatedStoploss !== null && (
                                  <span className="ml-2 text-emerald-600">
                                    (₹{tranche.calculatedStoploss})
                                  </span>
                                )}
                              </label>
                            </div>
                          )}
                          
                          {/* Manual Stoploss Input */}
                          {!tranche.maintainResidualRisk && (
                            <div>
                              <Label
                                htmlFor={`sl-${posIndex}-${trancheIndex}`}
                                className={isLatestBuy ? 'text-gray-900' : 'text-gray-600'}
                              >
                                {showResidualRisk ? 'Or enter stoploss manually' : 'Stoploss'}
                                {isLatestBuy && <span className="text-red-500"> *</span>}
                                {tranche.stoplossValue && parseFloat(tranche.stoplossValue) > 0 && (
                                  <span className="ml-2 text-sm font-normal text-emerald-600">
                                    ({((tranche.avgPrice - parseFloat(tranche.stoplossValue)) / tranche.avgPrice * 100).toFixed(1)}% below entry)
                                  </span>
                                )}
                              </Label>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="relative flex-1">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                                  <Input
                                    id={`sl-${posIndex}-${trancheIndex}`}
                                    type="number"
                                    step="0.01"
                                    placeholder={isLatestBuy ? 'Required' : 'Optional'}
                                    value={tranche.stoplossValue}
                                    onChange={(e) =>
                                      handleStoplossChange(posIndex, trancheIndex, e.target.value)
                                    }
                                    className={`pl-7 ${
                                      isLatestBuy && validationErrors[`${posIndex}-stoploss`]
                                        ? 'border-red-500'
                                        : ''
                                    }`}
                                  />
                                </div>
                              </div>
                              {isLatestBuy && validationErrors[`${posIndex}-stoploss`] && (
                                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {validationErrors[`${posIndex}-stoploss`]}
                                </p>
                              )}
                              {!isLatestBuy && !isFirstBuy && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Optional - Can be set later
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Sell tranche note */}
                      {!isBuy && !isAlreadyImported && tranche.selected && (
                        <p className="ml-8 text-xs text-gray-500">
                          No stoploss needed for sell orders
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Category Selection */}
              <div className={`border-t pt-4 ${!position.selected ? 'opacity-40 pointer-events-none' : ''}`}>
                <Label className="mb-2 block">
                  Category <span className="text-red-500">*</span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {portfolioCategories.length > 0 ? (
                    portfolioCategories.map((cat) => (
                      <button
                        key={cat.name}
                        type="button"
                        onClick={() => handleCategoryChange(posIndex, cat.name)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          position.category === cat.name
                            ? 'ring-2 ring-offset-2'
                            : 'opacity-70 hover:opacity-100'
                        }`}
                        style={{
                          backgroundColor: position.category === cat.name ? cat.color : `${cat.color}40`,
                          color: position.category === cat.name ? '#ffffff' : cat.color,
                          '--tw-ring-color': cat.color,
                        } as React.CSSProperties}
                      >
                        {cat.name}
                      </button>
                    ))
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleCategoryChange(posIndex, 'Core')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          position.category === 'Core'
                            ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        }`}
                      >
                        Core
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCategoryChange(posIndex, 'Satellite')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          position.category === 'Satellite'
                            ? 'bg-green-600 text-white ring-2 ring-green-600 ring-offset-2'
                            : 'bg-green-100 text-green-600 hover:bg-green-200'
                        }`}
                      >
                        Satellite
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCategoryChange(posIndex, 'Tactical')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          position.category === 'Tactical'
                            ? 'bg-purple-600 text-white ring-2 ring-purple-600 ring-offset-2'
                            : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                        }`}
                      >
                        Tactical
                      </button>
                    </>
                  )}
                </div>
                {validationErrors[`${posIndex}-category`] && (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {validationErrors[`${posIndex}-category`]}
                  </p>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-between items-center mt-6 pt-4 border-t">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack} disabled={isSaving}>
            ← Back
          </Button>
          <p className="text-sm text-gray-600">
            {totalSelectedPositions} position(s), {totalSelectedTranches} tranche(s) selected
            {existingPositionsTranches > 0 && newPositionsTranches > 0 && (
              <span className="text-gray-400 ml-1">
                ({existingPositionsCount} existing + {newPositionsCount} new)
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={handleImport}
          disabled={isSaving || totalSelectedTranches === 0}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Importing...
            </>
          ) : (
            `Import ${totalSelectedTranches} Tranche(s)`
          )}
        </Button>
      </div>
    </div>
  );
};

export default NewPositionsScreen;
